const DELIM_BEGIN = '<<<ST_AI_PROMPT_BEGIN>>>';
const DELIM_END = '<<<ST_AI_PROMPT_END>>>';
const DELIM_PRECISION_RE = /<<<ST_PRECISION:(\d{1,3})>>>/;

const extractQuotedJsonString = (raw: string, key: string): string | null => {
  const keyPattern = `"${key}"`;
  const keyIndex = raw.indexOf(keyPattern);
  if (keyIndex < 0) return null;

  let i = keyIndex + keyPattern.length;
  while (i < raw.length && /[\s:]/.test(raw[i])) i += 1;
  if (raw[i] !== '"') return null;
  i += 1;

  let out = '';
  while (i < raw.length) {
    const c = raw[i];
    if (c === '\\') {
      i += 1;
      if (i >= raw.length) break;
      const e = raw[i];
      switch (e) {
        case 'n': out += '\n'; break;
        case 't': out += '\t'; break;
        case 'r': out += '\r'; break;
        case '"': out += '"'; break;
        case '\\': out += '\\'; break;
        case '/': out += '/'; break;
        case 'u': {
          const hex = raw.slice(i + 1, i + 5);
          if (/^[0-9a-fA-F]{4}$/.test(hex)) {
            out += String.fromCharCode(parseInt(hex, 16));
            i += 4;
          } else {
            out += e;
          }
          break;
        }
        default:
          out += e;
      }
      i += 1;
    } else if (c === '"') {
      return out;
    } else {
      out += c;
      i += 1;
    }
  }

  return out.length > 0 ? out : null;
};

const extractUnquotedFieldBeforePrecision = (raw: string, key: string): string | null => {
  const marker = `"${key}"`;
  const idx = raw.indexOf(marker);
  if (idx < 0) return null;

  let start = idx + marker.length;
  while (start < raw.length && /[\s:]/.test(raw[start])) start += 1;
  if (raw[start] === '"') return null;

  const remainder = raw.slice(start);
  const endMatch = remainder.match(/\s*,\s*"(?:precision|score)"\s*:/);
  if (!endMatch || endMatch.index === undefined) return null;

  const value = remainder.slice(0, endMatch.index).trim();
  return value || null;
};

const parseDelimiterFormat = (raw: string): string | null => {
  const begin = raw.indexOf(DELIM_BEGIN);
  const end = raw.indexOf(DELIM_END);
  if (begin < 0 || end <= begin) return null;
  return raw.slice(begin + DELIM_BEGIN.length, end).trim() || null;
};

const looksLikeImprovementJson = (value: string): boolean =>
  value.trimStart().startsWith('{') && /"aiCreatedPrompt"|"improvedPrompt"/.test(value);

/**
 * Unwrap aiCreatedPrompt when the stored value is still a JSON/delimiter wrapper.
 */
export const normalizeAiCreatedPrompt = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) return '';

  const delimiter = parseDelimiterFormat(trimmed);
  if (delimiter) return delimiter;

  if (!looksLikeImprovementJson(trimmed)) return raw;

  const fenceMatch = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const candidate = (fenceMatch ? fenceMatch[1] : trimmed).trim();

  const tryParse = (text: string): string | null => {
    try {
      const parsed = JSON.parse(text) as Record<string, unknown>;
      const inner = String(parsed.aiCreatedPrompt ?? parsed.improvedPrompt ?? '').trim();
      return inner || null;
    } catch {
      return null;
    }
  };

  const direct = tryParse(candidate);
  if (direct) return normalizeAiCreatedPrompt(direct);

  const jsonStart = candidate.indexOf('{');
  const jsonEnd = candidate.lastIndexOf('}');
  if (jsonStart >= 0 && jsonEnd > jsonStart) {
    const sliced = tryParse(candidate.slice(jsonStart, jsonEnd + 1));
    if (sliced) return normalizeAiCreatedPrompt(sliced);
  }

  for (const key of ['aiCreatedPrompt', 'improvedPrompt'] as const) {
    const extracted =
      extractQuotedJsonString(candidate, key) ??
      extractUnquotedFieldBeforePrecision(candidate, key);
    if (extracted) return extracted.trim();
  }

  return raw;
};
